/* eslint-disable no-param-reassign, import/no-cycle */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import keyBy from 'lodash/keyBy';
import axios from 'axios';
import resourceSlug from '../../../constant/resource-slug';
import { INVOICE_SERVICE } from '../../../constant/invoice';
import { CurrencyDisplayModeEnum } from '../../../constant';
import { useHasCreateInvoicePermission, useHasReadInvoicePermission } from '../../group/components/utils';

export const useGetGroupRoyalty = (id, enabled) => {
  const [groupRoyalty, setGroupRoyalty] = useState({});

  const getGroup = async (id) => {
    try {
      const { data } = await axios.get(`/api/${resourceSlug.GROUP}/${id}`);

      setGroupRoyalty(keyBy(data?.royalty || [], 'invoiceService'));
    } catch (err) {
      console.error('[ERROR] Get Group Details', err?.message);
    }
  };

  useEffect(() => {
    if (id && enabled) {
      getGroup(id);
    }
    setGroupRoyalty({});
  }, [id, enabled]);

  return groupRoyalty;
};

export const useDefaultRoyalty = () => {
  const {
    input: { value: brandMonthlyRoyalty },
  } = useField('monthlyRoyalty');
  const {
    input: { value: brandMktRoyalty },
  } = useField('mktRoyalty');
  const {
    input: { value: group },
  } = useField('group');
  const hasReadInvoicePermission = useHasReadInvoicePermission();

  const groupRoyalty = useGetGroupRoyalty(
    group?.id,
    (!brandMonthlyRoyalty || !brandMktRoyalty) && hasReadInvoicePermission,
  );

  return useMemo(
    () => ({
      defaultMonthlyRoyalty: brandMonthlyRoyalty || groupRoyalty[INVOICE_SERVICE.MONTHLY_GGR]?.value,
      defaultMktRoyalty: brandMktRoyalty || groupRoyalty[INVOICE_SERVICE.MKT_FREE_SPIN]?.value,
    }),
    [groupRoyalty, brandMonthlyRoyalty, brandMktRoyalty],
  );
};

export const sanitizeRoyalty = (royalty = []) => {
  const cleaned = royalty.filter((item) => item.value);

  return cleaned.length > 0 ? cleaned : undefined;
};

export const useTransformBrandData = () => {
  const hasCreateInvoicePermission = useHasCreateInvoicePermission();

  return useCallback(
    (data) => {
      const { features, monthlyRoyalty, mktRoyalty, currenciesConfig, brandSettings, ...otherData } = data || {};

      return {
        ...otherData,
        brandSettings: {
          roundOpenPeriod: brandSettings?.roundOpenPeriod || 0,
        },
        featureIds: features?.map?.((feature) => feature?.id || feature),
        royalty: hasCreateInvoicePermission
          ? sanitizeRoyalty([
              {
                invoiceService: INVOICE_SERVICE.MONTHLY_GGR,
                value: monthlyRoyalty,
              },
              {
                invoiceService: INVOICE_SERVICE.MKT_FREE_SPIN,
                value: mktRoyalty,
              },
            ])
          : undefined,
        currencies: currenciesConfig?.map((currency) => {
          const royalty = sanitizeRoyalty([
            {
              invoiceService: INVOICE_SERVICE.MONTHLY_GGR,
              value: currency.monthlyRoyalty,
            },
            {
              invoiceService: INVOICE_SERVICE.MKT_FREE_SPIN,
              value: currency.mktRoyalty,
            },
          ]);

          const data = {
            id: currency.id,
          };

          if (royalty && hasCreateInvoicePermission) {
            data.royalty = royalty;
          }

          if (currency.mode && currency.mode !== CurrencyDisplayModeEnum.NORMAL) {
            data.mode = currency.mode;
          }

          return data;
        }),
      };
    },
    [hasCreateInvoicePermission],
  );
};
